/* eslint-disable complexity */
import React, {
  useContext,
  useEffect,
  useCallback,
  useState,
  useRef
} from 'react';
import makeStyles from '@material-ui/core/styles/makeStyles';
import { Grid } from '@material-ui/core';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import moment from 'moment';

import FlagColombia from '../../../../Components/CustomIcons/Flags/FlagColombia';
import SweetAlert from '../../../../Components/Alerts/SweetAlert';
import TextInput from '../../../../Components/Inputs/TextInput';
import CompositeSelectInput from '../../../../Components/Inputs/CompositeSelectInput';
import Title from '../../../../Components/Labels/Title';
import ModalProgress from '../../../../Components/Progress/Modal/ModalProgress';
import PhoneVerificationDialog from '../../../../Components/Dialogs/PhoneVerificationDialog';
import Recaptcha from '../../../../Components/Captcha/TurnstileManagedCaptcha';

import { UserContext } from '../../../../Contexts/UserContext';
import {
  SetCurrentStepIndexContext,
  StepperDataContext,
  StepperDataDispatchContext,
  CurrentStepIndexContext
} from '../../../../Contexts/StepperContext';

import { ValidateIdentificationAPI } from '../../../../API/UserAPI';
import { ValidateOwnership } from '../../../../API/Contracts/ContractsAPI';
import { extractErrorMessage } from '../../../../Utils/Errors/Errors';

import { IDENTIFICATION_OPTIONS } from '../Refinance_enums';
import { history } from '../../../../Routes/history';
import { ROUTE_NAMES } from '../../../../Routes/Routes';
import clsx from 'clsx';
import { ALERT_TYPE } from '../../../../Components/Alerts/alert_enums';
import { Company } from '../../../../Configs/general';
import { parseLocalDateTo } from '../../../../Utils/Date/parse';
import { NAMES_REGEX } from '../../../../Utils/Misc/RegEx';
import { yupResolver } from '@hookform/resolvers/yup';
import ControlledNumberInput from '../../../../Components/Controlled/ControlledNumberInput';
import { useCaptcha } from '../../../../Hooks/useCaptcha';

const schema = yup.object({
  firstName: yup
    .string()
    .trim()
    .matches(NAMES_REGEX, 'No se permiten números ni caracteres especiales')
    .max(80, 'Máximo 80 caracteres')
    .required('Ingresa los nombres'),
  lastName: yup
    .string()
    .trim()
    .matches(NAMES_REGEX, 'No se permiten números ni caracteres especiales')
    .max(80, 'Máximo 80 caracteres')
    .required('Ingresa los apellidos'),
  identificationType: yup
    .string()
    .oneOf(IDENTIFICATION_OPTIONS.map(idType => idType.value))
    .required('Elija un tipo valido'),
  identification: yup
    .number()
    .typeError('El número de identificación debe ser válido')
    .positive('El número de identificación debe ser válido')
    .test('integer', 'El número de identificación debe ser válido', val => {
      return val % 1 === 0;
    })
    .required('Ingresa el número de identificación'),
  expeditionDate: yup
    .date()
    .max(new Date(), 'Fecha no valida')
    .typeError('La fecha debe ser válida')
    .required('Elija una fecha valida'),
  phone: yup
    .string()
    .typeError('El número de teléfono debe ser válido')
    .trim()
    .length(10, 'Debe tener 10 dígitos')
    .required('Ingresa un teléfono'),
  phoneCountryCode: yup
    .string()
    .trim()
    .typeError('El código de país debe ser válido')
    .max(3, 'Debe tener máximo 3 dígitos')
    .required('Debe ser válido'),
  terms: yup.boolean().oneOf([true], 'Este campo es requerido.')
});

const PersonalDataStep = props => {
  const {
    formId,
    setCanSubmit,
    setGoBack,
    setCurrentStep: setVisualStepperIndex,
    setNextButtonText,
    setAlert,
    addedDeferreds,
    paramsSelectedContract,
    fromDeferredDebt,
    setSidebarVisible,
    selectedContract,
    onlyHaveGasProducts
  } = props;
  const classes = useStyles();

  const currentStepIndex = useContext(CurrentStepIndexContext);
  const setCurrentStep = useContext(SetCurrentStepIndexContext);
  const setData = useContext(StepperDataDispatchContext);
  const stepperData = useContext(StepperDataContext);

  const currentUser = useContext(UserContext);

  const { register, watch, errors, control, handleSubmit } = useForm({
    resolver: yupResolver(schema),
    shouldFocusError: true,
    defaultValues: {
      firstName: currentUser ? currentUser.firstName : '',
      lastName: currentUser ? currentUser.lastName : '',
      terms: false,
      identificationType:
        currentUser && currentUser.identificationType
          ? currentUser.identificationType
          : 'CC',
      identification: currentUser ? currentUser.identification : '',
      phoneCountryCode: currentUser.phoneCountryCode || '57',
      phone: currentUser ? currentUser.phone : '',
      ...stepperData
    }
  });

  const [loading, setLoading] = useState(false);
  const [openVerificationDialog, setOpenVerificationDialog] = useState(false);
  const [submittedValues, setSubmittedValues] = useState(null);
  const {
    captchaRef: recaptchaRef,
    handlers: captchaHandlers,
    captchaToken: captcha,
    loading: loadingCaptcha
  } = useCaptcha();

  const referenceRef = useRef(null);

  const watchForm = watch(['phoneCountryCode', 'phone']);

  const onSuccessUserData = useCallback(
    formValues => {
      const formattedDate = moment(formValues.expeditionDate).format(
        'YYYY-MM-DD'
      );

      setData(d => ({
        ...d,
        ...formValues,
        expeditionDate: formattedDate,
        contractId: selectedContract.id,
        contractCity: selectedContract.city,
        contractAddress: selectedContract.address
      }));
      setCurrentStep(step => step + 1);
    },
    [setData, selectedContract, setCurrentStep]
  );

  const handleOpenVerificationDialog = useCallback(
    values => {
      const {
        phone: currentPhone,
        phoneCountryCode: currentCountryCode
      } = values;
      const previousPhone = stepperData.phone;
      const previousCountryCode = stepperData.phoneCountryCode;
      const phoneHasChanged = previousPhone !== currentPhone;
      const countryCodeHasChanged = previousCountryCode !== currentCountryCode;

      if (!phoneHasChanged && !countryCodeHasChanged) {
        setSubmittedValues(null);
        onSuccessUserData(values);
        return;
      }
      setSubmittedValues(values);
      setOpenVerificationDialog(true);
    },
    [stepperData.phone, stepperData.phoneCountryCode, onSuccessUserData]
  );

  const verifyUserData = useCallback(
    async values => {
      const captchaValue = captcha;
      captchaHandlers.reset();

      const { identification, identificationType, expeditionDate } = values;
      const formattedDate = moment(expeditionDate).format('YYYY-MM-DD');

      const identificationHasChanged =
        stepperData.identification !== identification;

      const expeditionDateHasChanged =
        stepperData.expeditionDate !== formattedDate;

      if (identificationHasChanged || expeditionDateHasChanged) {
        setLoading(true);

        const identificationData = {
          identification,
          identificationType,
          expeditionDate: parseLocalDateTo(expeditionDate).toISOString()
        };

        const response = await ValidateIdentificationAPI(
          currentUser.token,
          identificationData,
          captchaValue
        );

        if (!response.success) {
          setLoading(false);
          setAlert({
            type: 'error',
            message: extractErrorMessage(response).message
          });
          return;
        }
      }

      if (!onlyHaveGasProducts && identificationHasChanged) {
        const queryData = {
          contractId: selectedContract.id,
          identification
        };

        const response = await ValidateOwnership(currentUser.token, queryData);

        if (!response.success) {
          setLoading(false);
          setAlert({
            type: 'error',
            message: extractErrorMessage(response).message
          });
          return;
        }

        const responseData = response.data.data;

        if (!responseData.isTheHolder) {
          setLoading(false);
          setAlert({
            type: 'error',
            message: `No eres el titular ${Company.contractConjugation.regular.singular.contraction}`
          });
          return;
        }

        setAlert({
          type: ALERT_TYPE.SUCCESS,
          message: 'Los datos del titular han sido validados correctamente.',
          toast: true,
          timeout: 3000
        });
      }

      setLoading(false);
      handleOpenVerificationDialog(values);
    },
    [
      currentUser.token,
      selectedContract,
      setAlert,
      stepperData.expeditionDate,
      stepperData.identification,
      handleOpenVerificationDialog,
      captcha,
      onlyHaveGasProducts,
      captchaHandlers
    ]
  );

  const onBackward = useCallback(() => {
    if (fromDeferredDebt) {
      history.replace(ROUTE_NAMES.deferredDebtSummary, {
        addedDeferreds,
        selectedContract: paramsSelectedContract
      });
    } else {
      history.goBack();
    }
  }, [addedDeferreds, fromDeferredDebt, paramsSelectedContract]);

  const handleCopyPaste = evt => {
    evt.preventDefault();
  };

  // Props setters
  useEffect(() => {
    setSidebarVisible(true);
    setNextButtonText('Siguiente');
    setVisualStepperIndex(currentStepIndex);
    setCanSubmit(true);
    setGoBack({
      action: onBackward
    });
  }, [
    setGoBack,
    onBackward,
    setVisualStepperIndex,
    currentStepIndex,
    setNextButtonText,
    setCanSubmit,
    setSidebarVisible
  ]);

  useEffect(() => {
    setCanSubmit(!loading && !loadingCaptcha && Boolean(captcha));
  }, [loading, loadingCaptcha, setCanSubmit, captcha]);

  if (!currentUser) {
    return <></>;
  }

  return (
    <div className={classes.root}>
      {loading && <ModalProgress message="Consultando" />}
      {loadingCaptcha && <ModalProgress message="Cargando" />}
      {openVerificationDialog && (
        <PhoneVerificationDialog
          open={openVerificationDialog}
          setDialog={setOpenVerificationDialog}
          setAlert={setAlert}
          title="Validar número celular"
          successCallback={() => onSuccessUserData(submittedValues)}
          formPhone={watchForm.phone}
          formPhoneCountryCode={watchForm.phoneCountryCode}
        />
      )}
      <Title title="Valida tus datos personales" className={classes.title} />

      <form id={formId} onSubmit={handleSubmit(verifyUserData)}>
        <Grid container>
          <Grid
            item
            xs={12}
            sm={6}
            className={clsx(classes.item, classes.leftItem)}
          >
            <TextInput
              label="Nombres*"
              inputProps={{ maxLength: 100 }}
              inputRef={register}
              fullWidth
              name="firstName"
              error={Boolean(errors.firstName)}
              helperText={errors.firstName && errors.firstName.message}
              onCut={handleCopyPaste}
              onCopy={handleCopyPaste}
              onPaste={handleCopyPaste}
              margin="none"
            />
          </Grid>
          <Grid item xs={12} sm={6} className={classes.item}>
            <TextInput
              label="Apellidos*"
              inputProps={{ maxLength: 100 }}
              inputRef={register}
              fullWidth
              name="lastName"
              error={Boolean(errors.lastName)}
              helperText={errors.lastName && errors.lastName.message}
              onCut={handleCopyPaste}
              onCopy={handleCopyPaste}
              onPaste={handleCopyPaste}
              margin="none"
            />
          </Grid>
          <Grid
            item
            xs={12}
            sm={6}
            className={clsx(classes.item, classes.leftItem)}
          >
            <CompositeSelectInput
              label="Identificación*"
              options={IDENTIFICATION_OPTIONS}
              TextInputProps={{
                label: 'Identificación*',
                name: 'identification',
                defaultValue: currentUser.identification || '',
                inputRef: register,
                fullWidth: true,
                margin: 'none'
              }}
              SelectInputProps={{
                name: 'identificationType',
                defaultValue: currentUser.identificationType || 'CC',
                control
              }}
              error={Boolean(errors.identification)}
              helperText={
                errors.identification && errors.identification.message
              }
            />
          </Grid>
          <Grid item xs={12} sm={6} className={classes.item}>
            <TextInput
              label="Fecha de expedición*"
              inputProps={{ maxLength: 100 }}
              fullWidth
              inputRef={register}
              type="date"
              name="expeditionDate"
              helperText={
                errors.expeditionDate && errors.expeditionDate.message
              }
              error={Boolean(errors.expeditionDate)}
              InputLabelProps={{ shrink: true }}
              margin="none"
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <Grid container>
              <Grid
                item
                xs={5}
                className={clsx(classes.item, classes.prefixItem)}
              >
                <ControlledNumberInput
                  control={control}
                  name="phoneCountryCode"
                  id="MyProfile_input_countryCode"
                  autoComplete="tel-country-code"
                  InputProps={{
                    startAdornment: (
                      <FlagColombia size={20} style={{ flexShrink: 0 }} />
                    )
                  }}
                  format="+###"
                  placeholder="57"
                  fullWidth
                  label="Prefijo"
                  error={Boolean(errors.phoneCountryCode)}
                  helperText={
                    errors.phoneCountryCode && errors.phoneCountryCode.message
                  }
                  margin="none"
                />
              </Grid>
              <Grid
                item
                xs={7}
                className={clsx(classes.item, classes.leftItem)}
              >
                <ControlledNumberInput
                  control={control}
                  name="phone"
                  id="MyProfile_input_phone"
                  autoComplete="tel-local"
                  fullWidth
                  margin="none"
                  label="Número celular"
                  error={Boolean(errors.phone)}
                  helperText={errors.phone && errors.phone.message}
                />
              </Grid>
            </Grid>
          </Grid>

          <Grid
            item
            xs={12}
            sm={6}
            ref={referenceRef}
            className={classes.recaptchaItem}
          >
            <Recaptcha
              captchaRef={recaptchaRef}
              referenceEl={referenceRef}
              heightScale={0.95}
              locale="es"
              onResolved={captchaHandlers.onResolved}
              onExpired={captchaHandlers.onExpired}
              onError={captchaHandlers.onError}
              onLoaded={captchaHandlers.onLoaded}
              style={{
                margin: 0,
                transformOrigin: 'left top'
              }}
            />
          </Grid>
        </Grid>
      </form>
      {!onlyHaveGasProducts && (
        <SweetAlert
          className={classes.negotiationSweetAlert}
          type={ALERT_TYPE.INFO}
          message={`Para hacer esta negociación necesitas ingresar los datos del titular ${Company.contractConjugation.regular.singular.contraction}.`}
        />
      )}
    </div>
  );
};

const useStyles = makeStyles(theme => ({
  root: {
    '&:last-child': {
      marginBottom: theme.spacing(2)
    }
  },
  title: {
    margin: theme.spacing(2, 0, 4)
  },
  item: {
    paddingBottom: theme.spacing(3)
  },
  prefixItem: {
    paddingRight: theme.spacing(2)
  },
  leftItem: {
    paddingRight: theme.spacing(2),
    [theme.breakpoints.down(theme.breakpoints.values.sm)]: {
      paddingRight: 0
    }
  },
  recaptchaItem: {
    [theme.breakpoints.down(theme.breakpoints.values.sm)]: {
      paddingBottom: theme.spacing(2)
    }
  }
}));

export default PersonalDataStep;
